import { useState } from "react";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Sparkles,
  Search,
  FileText,
  AlertTriangle,
  Scale,
  ClipboardCheck,
  Brain,
  Zap,
  ChevronRight,
  X,
  Building,
  Newspaper,
  Target,
  Shield,
} from "lucide-react";
import type { ResearchStatusProps } from "../types";
import { fadeInAnimation } from "../styles";

// Popup component for displaying detailed info
const InfoPopup = ({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[#0A0A12]/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg max-h-[80vh] bg-[#0F0F1A] border border-[#2D2D4A] rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-[#2D2D4A]">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#1A1A2E] rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-[#94A3B8]" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh] scrollbar-hide">
          {children}
        </div>
      </div>
    </div>
  );
};

// Counter badge component
const CounterBadge = ({
  icon: Icon,
  label,
  count,
  color,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  count: number;
  color: string;
  onClick?: () => void;
}) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1A1A2E]/50 border border-[#2D2D4A]/50 transition-all ${onClick ? "hover:border-[#7C3AED]/50 hover:bg-[#1A1A2E] cursor-pointer" : "cursor-default"}`}
  >
    <Icon className="h-3.5 w-3.5" style={{ color }} />
    <span className="text-xs text-[#94A3B8]">{label}</span>
    <span
      className="text-sm font-semibold px-1.5 py-0.5 rounded-md"
      style={{ backgroundColor: `${color}20`, color }}
    >
      {count}
    </span>
  </button>
);

// Phase step component with timeline
const PhaseStep = ({
  icon: Icon,
  title,
  message,
  status,
  color,
  children,
  isLast,
}: {
  icon: React.ElementType;
  title: string;
  message?: string;
  status: "pending" | "active" | "complete" | "error";
  color: string;
  children?: React.ReactNode;
  isLast?: boolean;
}) => (
  <div className="relative flex gap-4">
    {/* Timeline connector */}
    {!isLast && (
      <div
        className={`absolute left-[15px] top-10 bottom-0 w-px ${status === "complete" ? "bg-gradient-to-b from-[#10B981] to-[#10B981]/20" : "bg-[#2D2D4A]/50"}`}
      />
    )}

    {/* Status icon */}
    <div className="relative z-10 flex-shrink-0">
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
          status === "complete"
            ? "bg-[#10B981]/20 border border-[#10B981]/50"
            : status === "active"
              ? "bg-[#7C3AED]/20 border border-[#7C3AED]/50"
              : status === "error"
                ? "bg-[#EF4444]/20 border border-[#EF4444]/50"
                : "bg-[#1A1A2E] border border-[#2D2D4A]/50"
        }`}
      >
        {status === "active" ? (
          <Loader2 className="h-4 w-4 animate-spin" style={{ color }} />
        ) : status === "complete" ? (
          <CheckCircle2 className="h-4 w-4 text-[#10B981]" />
        ) : status === "error" ? (
          <XCircle className="h-4 w-4 text-[#EF4444]" />
        ) : (
          <Icon className="h-4 w-4 text-[#3D3D5C]" />
        )}
      </div>
    </div>

    {/* Content */}
    <div className="flex-1 pb-6">
      <div className="flex items-center gap-2 mb-1">
        <span
          className={`text-sm font-medium ${
            status === "complete"
              ? "text-[#10B981]"
              : status === "active"
                ? "text-[#A78BFA]"
                : status === "error"
                  ? "text-[#EF4444]"
                  : "text-[#3D3D5C]"
          }`}
        >
          {title}
        </span>
        {status === "active" && (
          <div className="flex gap-0.5">
            <span
              className="w-1 h-1 rounded-full animate-bounce"
              style={{ backgroundColor: color, animationDelay: "0ms" }}
            />
            <span
              className="w-1 h-1 rounded-full animate-bounce"
              style={{ backgroundColor: color, animationDelay: "150ms" }}
            />
            <span
              className="w-1 h-1 rounded-full animate-bounce"
              style={{ backgroundColor: color, animationDelay: "300ms" }}
            />
          </div>
        )}
      </div>
      {message && <p className="text-xs text-[#64748B] mb-2">{message}</p>}
      {children && <div className="mt-2">{children}</div>}
    </div>
  </div>
);

const ResearchStatus = ({
  status,
  error,
  isComplete,
  isResetting,
  loaderColor,
  statusRef,
  events,
  progress,
}: ResearchStatusProps) => {
  const [showQueriesPopup, setShowQueriesPopup] = useState(false);
  const [showReasoningPopup, setShowReasoningPopup] = useState(false);

  if (!status) return null;

  const isError = !!error;
  const isDone = status?.step === "Complete" || isComplete;

  // Determine current phase from events
  const getPhaseStatus = (
    phase: string,
  ): "pending" | "active" | "complete" | "error" => {
    if (isError) return "error";
    const phaseOrder = [
      "intent",
      "research",
      "extraction",
      "contradiction",
      "reasoning",
      "checklist",
    ];
    const currentPhase = getCurrentPhase();
    const currentIndex = phaseOrder.indexOf(currentPhase);
    const phaseIndex = phaseOrder.indexOf(phase);

    if (isDone) return "complete";
    if (phaseIndex < currentIndex) return "complete";
    if (phaseIndex === currentIndex) return "active";
    return "pending";
  };

  const getCurrentPhase = (): string => {
    const lastEvent = events[events.length - 1];
    if (!lastEvent) return "intent";

    const type = lastEvent.type;
    if (type?.startsWith("intent")) return "intent";
    if (type?.startsWith("research") || type?.startsWith("quer"))
      return "research";
    if (type?.startsWith("extraction")) return "extraction";
    if (type?.startsWith("contradiction")) return "contradiction";
    if (type?.startsWith("reasoning")) return "reasoning";
    if (type?.startsWith("checklist")) return "checklist";
    if (type === "complete") return "complete";
    return "intent";
  };

  // Get last relevant message for each phase
  const getPhaseMessage = (phase: string): string | undefined => {
    const phaseEvents = events.filter((e) => {
      const type = e.type || "";
      if (phase === "intent") return type.startsWith("intent");
      if (phase === "research")
        return type.startsWith("research") || type.startsWith("quer");
      if (phase === "extraction") return type.startsWith("extraction");
      if (phase === "contradiction") return type.startsWith("contradiction");
      if (phase === "reasoning") return type.startsWith("reasoning");
      if (phase === "checklist") return type.startsWith("checklist");
      return false;
    });
    const lastEvent = phaseEvents[phaseEvents.length - 1];
    return lastEvent?.message;
  };

  return (
    <div
      ref={statusRef}
      className={`relative ${fadeInAnimation.fadeIn} ${isResetting ? "opacity-0 transform -translate-y-4" : "opacity-100 transform translate-y-0"} transition-all duration-300`}
    >
      {/* Main container */}
      <div className="rounded-2xl bg-[#0F0F1A]/80 border border-[#2D2D4A]/50 overflow-hidden backdrop-blur-sm">
        {/* Header bar */}
        <div
          className={`h-1 ${isError ? "bg-[#EF4444]" : isDone ? "bg-gradient-to-r from-[#10B981] to-[#06B6D4]" : "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4]"}`}
        />

        <div className="p-5">
          {/* Title */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              {isDone ? (
                <Sparkles className="h-5 w-5 text-[#10B981]" />
              ) : isError ? (
                <XCircle className="h-5 w-5 text-[#EF4444]" />
              ) : (
                <Loader2
                  className="h-5 w-5 animate-spin"
                  style={{ color: loaderColor }}
                />
              )}
              <h3
                className={`text-base font-semibold ${isDone ? "text-[#10B981]" : isError ? "text-[#EF4444]" : "text-white"}`}
              >
                {isDone
                  ? "Analysis Complete"
                  : isError
                    ? "Analysis Failed"
                    : "Analyzing..."}
              </h3>
            </div>
            {error && (
              <span className="text-xs text-[#EF4444] bg-[#EF4444]/10 px-2 py-1 rounded-md">
                {error}
              </span>
            )}
          </div>

          {/* Phase timeline */}
          <div className="space-y-0">
            {/* Intent Phase */}
            <PhaseStep
              icon={Brain}
              title="Intent Analysis"
              message={getPhaseMessage("intent")}
              status={getPhaseStatus("intent")}
              color={loaderColor}
            >
              {progress.intent && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-[#A78BFA] bg-[#7C3AED]/10 px-2 py-1 rounded-md">
                    {progress.intent}
                  </span>
                  {progress.topics && progress.topics.length > 0 && (
                    <span className="text-xs text-[#94A3B8]">
                      {progress.topics.length} topics detected
                    </span>
                  )}
                </div>
              )}
            </PhaseStep>

            {/* Research Phase */}
            <PhaseStep
              icon={Search}
              title="Legal Research"
              message={getPhaseMessage("research")}
              status={getPhaseStatus("research")}
              color={loaderColor}
            >
              {progress.queries && progress.queries.length > 0 && (
                <div className="space-y-2">
                  {/* Queries button */}
                  <button
                    onClick={() => setShowQueriesPopup(true)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1A1A2E]/50 border border-[#2D2D4A]/50 hover:border-[#7C3AED]/50 hover:bg-[#1A1A2E] transition-all group"
                  >
                    <Zap className="h-3.5 w-3.5 text-[#F59E0B]" />
                    <span className="text-xs text-[#94A3B8]">
                      {progress.queries.length} queries generated
                    </span>
                    <ChevronRight className="h-3 w-3 text-[#3D3D5C] group-hover:text-[#7C3AED] transition-colors" />
                  </button>

                  {/* Source counters */}
                  {(progress.officialSourcesCount > 0 ||
                    progress.newsSourcesCount > 0) && (
                    <div className="flex flex-wrap gap-2">
                      {progress.officialSourcesCount > 0 && (
                        <CounterBadge
                          icon={Building}
                          label="Official"
                          count={progress.officialSourcesCount}
                          color="#7C3AED"
                        />
                      )}
                      {progress.newsSourcesCount > 0 && (
                        <CounterBadge
                          icon={Newspaper}
                          label="News"
                          count={progress.newsSourcesCount}
                          color="#06B6D4"
                        />
                      )}
                      {progress.totalSourcesCount > 0 && (
                        <CounterBadge
                          icon={FileText}
                          label="Total"
                          count={progress.totalSourcesCount}
                          color="#10B981"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </PhaseStep>

            {/* Extraction Phase */}
            <PhaseStep
              icon={FileText}
              title="Claim Extraction"
              message={getPhaseMessage("extraction")}
              status={getPhaseStatus("extraction")}
              color={loaderColor}
            >
              {progress.claimsCount > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {/* Total claims */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1A1A2E]/50 border border-[#2D2D4A]/50">
                    <Target className="h-3.5 w-3.5 text-[#A78BFA]" />
                    <span className="text-xs text-[#94A3B8]">Claims</span>
                    <span className="text-sm font-semibold text-[#A78BFA] ml-auto">
                      {progress.claimsCount}
                    </span>
                  </div>
                  {/* Category breakdown */}
                  {progress.claimsByCategory &&
                    Object.entries(progress.claimsByCategory).map(
                      ([cat, count]) => {
                        const colors = {
                          illegal: {
                            bg: "bg-[#EF4444]/10",
                            border: "border-[#EF4444]/30",
                            text: "text-[#EF4444]",
                          },
                          restricted: {
                            bg: "bg-[#F59E0B]/10",
                            border: "border-[#F59E0B]/30",
                            text: "text-[#F59E0B]",
                          },
                          legal: {
                            bg: "bg-[#10B981]/10",
                            border: "border-[#10B981]/30",
                            text: "text-[#10B981]",
                          },
                        };
                        const style =
                          colors[cat as keyof typeof colors] || colors.legal;
                        return (
                          <div
                            key={cat}
                            className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg ${style.bg} border ${style.border}`}
                          >
                            <span
                              className={`text-xs capitalize ${style.text}`}
                            >
                              {cat}
                            </span>
                            <span
                              className={`text-sm font-semibold ${style.text}`}
                            >
                              {count}
                            </span>
                          </div>
                        );
                      },
                    )}
                </div>
              )}
            </PhaseStep>

            {/* Contradiction Phase */}
            <PhaseStep
              icon={AlertTriangle}
              title="Contradiction Analysis"
              message={getPhaseMessage("contradiction")}
              status={getPhaseStatus("contradiction")}
              color={loaderColor}
            >
              {getPhaseStatus("contradiction") === "complete" && (
                <CounterBadge
                  icon={AlertTriangle}
                  label="Contradictions"
                  count={progress.contradictionsCount}
                  color={
                    progress.contradictionsCount > 0 ? "#F59E0B" : "#10B981"
                  }
                />
              )}
            </PhaseStep>

            {/* Reasoning Phase */}
            <PhaseStep
              icon={Scale}
              title="Legal Reasoning"
              message={getPhaseMessage("reasoning")}
              status={getPhaseStatus("reasoning")}
              color={loaderColor}
            >
              {progress.verdict && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Verdict badge */}
                    <span
                      className={`text-sm font-semibold px-3 py-1 rounded-lg ${
                        progress.verdict === "Legal"
                          ? "bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30"
                          : progress.verdict === "Illegal"
                            ? "bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30"
                            : "bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30"
                      }`}
                    >
                      {progress.verdict}
                    </span>
                    {/* Confidence */}
                    {progress.confidence !== undefined && (
                      <span className="text-xs text-[#94A3B8]">
                        {Math.round(progress.confidence * 100)}% confidence
                      </span>
                    )}
                  </div>

                  {/* Key factors button */}
                  {progress.keyFactors && progress.keyFactors.length > 0 && (
                    <button
                      onClick={() => setShowReasoningPopup(true)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1A1A2E]/50 border border-[#2D2D4A]/50 hover:border-[#7C3AED]/50 hover:bg-[#1A1A2E] transition-all group"
                    >
                      <Shield className="h-3.5 w-3.5 text-[#A78BFA]" />
                      <span className="text-xs text-[#94A3B8]">
                        {progress.keyFactors.length} key factors
                      </span>
                      <ChevronRight className="h-3 w-3 text-[#3D3D5C] group-hover:text-[#7C3AED] transition-colors" />
                    </button>
                  )}
                </div>
              )}
            </PhaseStep>

            {/* Checklist Phase */}
            <PhaseStep
              icon={ClipboardCheck}
              title="Compliance Checklist"
              message={getPhaseMessage("checklist")}
              status={getPhaseStatus("checklist")}
              color={loaderColor}
              isLast
            >
              {progress.checklistTotal > 0 && (
                <div className="flex flex-wrap gap-2">
                  <CounterBadge
                    icon={ClipboardCheck}
                    label="Total"
                    count={progress.checklistTotal}
                    color="#A78BFA"
                  />
                  {progress.checklistRequired > 0 && (
                    <CounterBadge
                      icon={Shield}
                      label="Required"
                      count={progress.checklistRequired}
                      color="#EF4444"
                    />
                  )}
                  {progress.checklistRecommended > 0 && (
                    <CounterBadge
                      icon={Target}
                      label="Recommended"
                      count={progress.checklistRecommended}
                      color="#F59E0B"
                    />
                  )}
                </div>
              )}
            </PhaseStep>
          </div>
        </div>
      </div>

      {/* Queries Popup */}
      <InfoPopup
        isOpen={showQueriesPopup}
        onClose={() => setShowQueriesPopup(false)}
        title="Search Queries"
      >
        <div className="space-y-3">
          {progress.queries?.map((query, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3 p-3 rounded-lg bg-[#1A1A2E]/50 border border-[#2D2D4A]/50"
            >
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#7C3AED]/20 text-[#A78BFA] text-xs font-medium flex items-center justify-center">
                {idx + 1}
              </span>
              <p className="text-sm text-[#E2E8F0] font-mono">{query}</p>
            </div>
          ))}
        </div>
      </InfoPopup>

      {/* Reasoning Popup */}
      <InfoPopup
        isOpen={showReasoningPopup}
        onClose={() => setShowReasoningPopup(false)}
        title="Legal Reasoning Details"
      >
        <div className="space-y-4">
          {/* Verdict */}
          <div className="p-4 rounded-lg bg-[#1A1A2E]/50 border border-[#2D2D4A]/50">
            <p className="text-xs text-[#64748B] uppercase tracking-wider mb-2">
              Verdict
            </p>
            <div className="flex items-center gap-3">
              <span
                className={`text-lg font-bold ${
                  progress.verdict === "Legal"
                    ? "text-[#10B981]"
                    : progress.verdict === "Illegal"
                      ? "text-[#EF4444]"
                      : "text-[#F59E0B]"
                }`}
              >
                {progress.verdict}
              </span>
              {progress.confidence !== undefined && (
                <span className="text-sm text-[#94A3B8]">
                  ({Math.round(progress.confidence * 100)}% confidence)
                </span>
              )}
            </div>
          </div>

          {/* Key Factors */}
          {progress.keyFactors && progress.keyFactors.length > 0 && (
            <div>
              <p className="text-xs text-[#64748B] uppercase tracking-wider mb-2">
                Key Factors
              </p>
              <div className="space-y-2">
                {progress.keyFactors.map((factor, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 p-3 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/20"
                  >
                    <Shield className="h-4 w-4 text-[#A78BFA] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#E2E8F0]">{factor}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Primary Sources */}
          {progress.primarySources && progress.primarySources.length > 0 && (
            <div>
              <p className="text-xs text-[#64748B] uppercase tracking-wider mb-2">
                Primary Sources
              </p>
              <div className="space-y-2">
                {progress.primarySources.map((source, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 p-3 rounded-lg bg-[#06B6D4]/10 border border-[#06B6D4]/20"
                  >
                    <FileText className="h-4 w-4 text-[#06B6D4] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#E2E8F0]">{source}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </InfoPopup>
    </div>
  );
};

export default ResearchStatus;
